/**
 * Theme Context - Dark mode theme management
 * Persists theme preference in localStorage with key 'monkey-theme'
 * Respects system preference by default with 300ms smooth transitions
 * Handles StatusBar style changes on native platforms
 * Also manages large text mode (accessibility) with key 'large-text-enabled'
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  largeText: boolean;
  toggleLargeText: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'monkey-theme';
const LARGE_TEXT_STORAGE_KEY = 'large-text-enabled';

// Detect system preference
const getSystemTheme = (): Theme => {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Get initial theme from localStorage or default to dark on native
const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'dark';

  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
  if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
    return savedTheme;
  }

  // Default to dark mode on native platforms, system preference on web
  if (Capacitor.isNativePlatform()) {
    return 'dark';
  }

  return getSystemTheme();
};

// Get initial large text preference from localStorage (default off)
const getInitialLargeText = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(LARGE_TEXT_STORAGE_KEY) === 'true';
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [largeText, setLargeText] = useState<boolean>(getInitialLargeText);

  // Apply theme to document root and update StatusBar on native
  useEffect(() => {
    const root = document.documentElement;

    // Add transition class for smooth theme switching (300ms)
    root.style.transition = 'background-color 300ms ease-in-out, color 300ms ease-in-out';

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Save to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, theme);

    // Update StatusBar on native platforms
    if (Capacitor.isNativePlatform()) {
      const updateStatusBar = async () => {
        try {
          if (theme === 'dark') {
            // Dark theme: light icons on dark background
            await StatusBar.setStyle({ style: Style.Dark });
            await StatusBar.setBackgroundColor({ color: '#0a0a0a' });
          } else {
            // Light theme: dark icons on light background
            await StatusBar.setStyle({ style: Style.Light });
            await StatusBar.setBackgroundColor({ color: '#ffffff' });
          }
        } catch (error) {
          console.warn('StatusBar update failed:', error);
        }
      };
      updateStatusBar();
    }

    // Update theme-color meta tag for PWA/browser
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme === 'dark' ? '#0a0a0a' : '#ffffff');
    }
  }, [theme]);

  // Apply large text mode to document root and persist preference
  useEffect(() => {
    const root = document.documentElement;

    if (largeText) {
      root.classList.add('large-text');
    } else {
      root.classList.remove('large-text');
    }

    // Save to localStorage
    localStorage.setItem(LARGE_TEXT_STORAGE_KEY, String(largeText));
  }, [largeText]);

  // Listen to system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      // Only update if user hasn't explicitly set a preference
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'dark' ? 'light' : 'dark'));
  };

  const toggleLargeText = () => {
    setLargeText((prev) => !prev);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, largeText, toggleLargeText }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
